/* Cookie Policy — explains the cookies/trackers used by zhelta.com and the
 * 3 division sub-properties. Linked from the Privacy Policy and from all 4
 * footers. Mirrors the visual language of /privacy and /copyright (eyebrow
 * label → big title → light tan info card on contact). */
import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { Link } from 'react-router-dom';
import { Mail, Shield, Cookie, Globe, BarChart3 } from 'lucide-react';
import { getOrganizationSchema } from '../data/orgSchema';
import { getBreadcrumbSchema } from '../data/breadcrumbSchema';

export default function Cookies() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Cookie Policy', path: '/cookies' },
    ]),
  ];

  // Helper: open the cookie banner again so a returning visitor can change
  // their consent. Banner reads localStorage on mount; clearing the key
  // makes it reappear.
  const reopenBanner = () => {
    try {
      localStorage.removeItem('zhelta-cookie-consent');
      window.dispatchEvent(new Event('zhelta-cookie-consent-cleared'));
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } catch {
      // localStorage blocked — best-effort no-op
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path="/cookies"
        title="Cookie Policy — ZHELTA"
        description="How ZHELTA LLC uses cookies and similar tracking technologies on zhelta.com and across our divisions."
        jsonLd={jsonLd}
      />
      <Header />

      <main className="py-16 md:py-24" data-testid="cookies-page">
        <div className="zh-container max-w-[820px]">
          <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#c9a227]">
            Legal
          </span>

          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Cookie <span className="text-[#f5a623]">Policy</span>
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-[12.5px] text-gray-500">
            <div>
              <span className="uppercase tracking-[0.18em] font-extrabold text-gray-400 mr-1.5">Last Updated</span>
              {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          <p className="mt-8 text-[15px] text-gray-600 leading-relaxed">
            This Cookie Policy explains what cookies are, which ones we use on{' '}
            <strong className="text-gray-900">zhelta.com</strong> (including the Systems, Services, and
            Academy divisions), and how you can manage your preferences. For a broader explanation
            of how we handle your information, please see our{' '}
            <Link to="/privacy" className="text-[#a8871a] hover:text-[#c9a227] underline underline-offset-2">Privacy Policy</Link>.
          </p>

          <div className="mt-12 space-y-12 text-[15px] text-gray-700 leading-[1.75]">
            {/* What are cookies */}
            <Section testId="cookies-section-what" title="What Are Cookies?">
              <p>
                Cookies are small text files placed on your device when you visit a website. They
                allow the site to remember your actions and preferences (such as language, region,
                or login state) over a period of time, so you don&rsquo;t have to re-enter them
                every time you come back or browse from one page to another.
              </p>
            </Section>

            {/* Categories */}
            <Section testId="cookies-section-categories" title="Categories of Cookies We Use">
              <div className="mt-3 grid sm:grid-cols-2 gap-4">
                <CookieCard
                  icon={<Shield className="w-4 h-4" />}
                  label="Strictly Necessary"
                  always
                  body="Required for the site to function — sign-in sessions, checkout, fraud prevention. These cannot be turned off."
                />
                <CookieCard
                  icon={<Globe className="w-4 h-4" />}
                  label="Preferences"
                  body="Remember your language, currency, and region selections so you don't have to set them on every visit."
                />
                <CookieCard
                  icon={<BarChart3 className="w-4 h-4" />}
                  label="Analytics"
                  body="Help us understand which pages are popular, how visitors move through the site, and where to focus our improvements. Aggregate, never personally identifying."
                />
                <CookieCard
                  icon={<Cookie className="w-4 h-4" />}
                  label="Marketing"
                  body="Used only when you have explicitly consented. Help us measure campaign performance and reach relevant audiences off-site."
                />
              </div>
            </Section>

            {/* Third parties */}
            <Section testId="cookies-section-thirdparty" title="Third-Party Cookies">
              <p>
                Some cookies are set by third-party services we use to operate ZHELTA. These
                include payment processors, support widgets, and translation providers. The data
                they collect is governed by their own privacy policies.
              </p>
            </Section>

            {/* Manage */}
            <Section testId="cookies-section-manage" title="Managing Your Preferences">
              <p>You can manage cookies in three ways:</p>
              <ul className="mt-2 list-disc pl-6 space-y-1.5 marker:text-[#f5a623]">
                <li>
                  <button
                    type="button"
                    onClick={reopenBanner}
                    data-testid="cookies-reopen-banner"
                    className="text-[#a8871a] hover:text-[#c9a227] underline underline-offset-2 font-semibold"
                  >
                    Reopen the cookie banner
                  </button>{' '}
                  on this page to change your decision.
                </li>
                <li>Use your browser settings to block, accept, or delete cookies for any website.</li>
                <li>Visit your operating-system privacy controls for global tracking preferences.</li>
              </ul>
              <p className="mt-4 text-[13.5px] text-gray-500">
                Note: Blocking strictly necessary cookies will prevent the site from functioning
                correctly (e.g. logging in or placing an order).
              </p>
            </Section>

            {/* Changes */}
            <Section testId="cookies-section-changes" title="Changes to This Cookie Policy">
              <p>
                We may update this Cookie Policy from time to time. The &ldquo;Last Updated&rdquo;
                date at the top of this page reflects the most recent revision.
              </p>
            </Section>

            {/* Contact */}
            <Section testId="cookies-section-contact" title="Contact Us">
              <p>If you have any questions about our use of cookies, please contact us at:</p>

              <div className="mt-5 rounded-2xl border border-gray-200 bg-[#FEFBF3] p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#c9a227] flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Email</div>
                    <a
                      href="mailto:legals@zhelta.com"
                      className="text-[15px] font-semibold text-gray-900 hover:text-[#c9a227]"
                      data-testid="cookies-contact-email"
                    >
                      legals@zhelta.com
                    </a>
                  </div>
                </div>
              </div>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---------- presentational helpers ---------- */

function Section({ title, testId, children }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function CookieCard({ icon, label, body, always = false }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-full bg-[#FEFBF3] text-[#c9a227] inline-flex items-center justify-center">
          {icon}
        </div>
        <div className="text-[13px] font-extrabold uppercase tracking-[0.14em] text-gray-900">
          {label}
        </div>
        {always && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[#0e0e10] text-[#f5a623]">
            Always on
          </span>
        )}
      </div>
      <p className="text-[13.5px] text-gray-600 m-0">{body}</p>
    </div>
  );
}
